/** @file sheetBriefOf: the brief asking an image model for one exploded parts sheet per facing. */
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { ArtBriefProfile } from '../artBriefOf';
import { briefCamera } from '../briefCamera';
import { facingWords } from '../facingWords';
import { primaryPartsOf } from './primaryPartsOf';
import { restPlacementOf } from './restPlacementOf';
import { SHEET_FACINGS } from './sheetFacings';
import { sheetPartWords } from './sheetPartWords';
import type { PlacedPart } from './sheetTypes';

const INTRO = [
  '# Art brief: one exploded sheet per facing',
  '',
  'Draw ONE image per facing below. In it the character is taken apart: every part listed floats on its',
  'own, separated from the others by a clear gap of empty space, on a transparent background. Draw each',
  'part whole, in the proportions it has on the assembled character (the height shares below are the',
  'guide), all parts at one common scale, laid out roughly where they sit on the body: head on top,',
  'torso below it, arms beside it, legs at the bottom. No overlaps, no shadows, no text, no frame.',
  "Left and right are the character's own. Save each image under the file name in its heading.",
  'A model that ignores the proportions: use the guide sheet mode instead (it fixes them).',
];

/** Centre of a part's quad. */
const centre = (p: PlacedPart): [number, number] => [p.quad.reduce((s, q) => s + q[0], 0) / 4, p.quad.reduce((s, q) => s + q[1], 0) / 4];

/** Reading order: top to bottom by centre (within 2 px is one row), then left to right. */
const reading = (a: PlacedPart, b: PlacedPart): number => {
  const [ax, ay] = centre(a);
  const [bx, by] = centre(b);
  return Math.abs(ay - by) > 2 ? ay - by : ax - bx;
};

/** The section of one facing: heading with the file name, then the parts numbered in reading order. */
function facingSection(puppet: Puppet, deg: number, label: string): string[] {
  const placement = restPlacementOf(puppet, deg);
  const parts = primaryPartsOf(placement.parts).sort(reading);
  return [`## \`sheet.${String(deg)}.png\`: ${label}, ${facingWords(deg)}`, '',
    'Parts in reading order (top to bottom, then left to right on the screen):', '',
    ...parts.map((p, i) => `${String(i + 1)}. ${sheetPartWords(p, placement)}`)];
}

/**
 * The exploded-sheet brief: intro, style, camera, then one section per facing of `SHEET_FACINGS` naming the file
 * `sheet.<facingDeg>.png` and listing the parts (one per node; layered drawables as one piece) in reading order.
 */
export function sheetBriefOf(puppet: Puppet, style: string, profile: ArtBriefProfile): string {
  const lines = [...INTRO, '', '## Style', '', style.trim() === '' ? 'No style notes.' : style.trim(), '', ...briefCamera(profile)];
  for (const f of SHEET_FACINGS) lines.push('', ...facingSection(puppet, f.deg, f.label));
  return [...lines, ''].join('\n');
}
